import themeGet from "@styled-system/theme-get";
import { Small, Icon } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

import Icons from "src/config/icons";
import ExpandButton from "./ExpandButton";

const Container = styled.div`
  position: relative;
  max-height: ${(props) =>
    props.isCollapsed ? `${props.maxHeight}px` : "none"};
  overflow: ${(props) => (props.isCollapsed ? "hidden" : "visible")};
  padding-bottom: ${(props) => {
    if (props.noCollapse && !props.hasFooter) {
      return "0px";
    }
    if (props.hasFooter && !props.isAbsoluteFooter) {
      return "0px";
    }
    return props.isAbsoluteFooter ? "20px" : "0px";
  }};
  transition: max-height 0.2s ease-in-out;
`;

const FadeOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 100px;
  width: 100%;
  cursor: pointer;
  z-index: 1;
  display: ${(props) => (props.visible ? "block" : "none")};
  background-image: linear-gradient(
    to bottom,
    transparent 10%,
    ${themeGet("colors.surface")} 80%
  );
`;

const StyledExpandButton = styled(ExpandButton)`
  align-items: center;
`;

const FooterRow = styled.div`
  position: ${(props) => (props.isAbsolute ? "absolute" : "static")};
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  z-index: 1;
  padding-top: ${(props) => (props.isAbsolute ? "0" : "8px")};
  padding-bottom: 0;
  background: ${(props) =>
    props.isAbsolute
      ? `linear-gradient(to bottom, transparent, ${themeGet("colors.surface")(props)})`
      : "transparent"};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${themeGet("space.r")};
  padding: 0;
  margin: 0 0 ${themeGet("space.r")} 0;
  position: ${(props) => (props.stickyTop ? "sticky" : "static")};
  top: ${(props) => props.stickyTop || "0px"};
  z-index: 4;
  background: ${themeGet("colors.surface")};
`;

const FooterContentWrapper = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
`;

const renderToolbar = (expandButton, footerContent, Row, rowProps = {}) => {
  if (!expandButton && !footerContent) {
    return null;
  }

  return (
    <Row {...rowProps}>
      {expandButton}
      {footerContent && (
        <FooterContentWrapper>{footerContent}</FooterContentWrapper>
      )}
    </Row>
  );
};

const ExpandableContent = ({
  children,
  maxHeight = 150,
  initialExpanded = false,
  footerContent = null,
  buttonPosition = "bottom",
  renderExpandButton: renderExpandButtonProp = null,
  stickyTop = null,
  onCollapsedChange = null,
}) => {
  const [isCollapsed, setCollapsed] = useState(!initialExpanded);
  const [noCollapse, setNoCollapse] = useState(false);
  const containerRef = useRef();

  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    setNoCollapse(el.scrollHeight <= maxHeight);
  }, [maxHeight, children]);

  const onClick = () => {
    setCollapsed(!isCollapsed);
  };

  const title = isCollapsed ? "Show more" : "Show less";

  const shouldRenderToolbar = !noCollapse || footerContent;
  const isAbsoluteFooter = !noCollapse && isCollapsed;

  const defaultExpandButton = (
    <StyledExpandButton
      aria-label={`${title} - visual effect only`}
      onClick={onClick}
      isCollapsed={isCollapsed}
    >
      <Small mr="xs">{title}</Small>
      <Icon icon={isCollapsed ? Icons.chevronDown : Icons.chevronUp} />
    </StyledExpandButton>
  );

  const expandButton = !noCollapse
    ? renderExpandButtonProp
      ? renderExpandButtonProp({ isCollapsed, onClick, title })
      : defaultExpandButton
    : null;

  const toolbar = renderToolbar(expandButton, footerContent, FooterRow, {
    isAbsolute: isAbsoluteFooter,
  });

  return (
    <>
      {buttonPosition === "top" &&
        shouldRenderToolbar &&
        renderToolbar(expandButton, footerContent, HeaderRow, {
          stickyTop,
        })}
      <Container
        ref={containerRef}
        isCollapsed={isCollapsed}
        maxHeight={maxHeight}
        noCollapse={noCollapse}
        hasFooter={Boolean(footerContent)}
        isAbsoluteFooter={buttonPosition === "bottom" && isAbsoluteFooter}
      >
        {children}
        <FadeOverlay
          visible={isCollapsed && !noCollapse}
          onClick={onClick}
          aria-hidden="true"
        />
        {buttonPosition === "bottom" && shouldRenderToolbar && toolbar}
      </Container>
    </>
  );
};

ExpandableContent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  maxHeight: PropTypes.number,
  initialExpanded: PropTypes.bool,
  footerContent: PropTypes.node,
  buttonPosition: PropTypes.oneOf(["top", "bottom"]),
  renderExpandButton: PropTypes.func,
  stickyTop: PropTypes.string,
  onCollapsedChange: PropTypes.func,
};

export default ExpandableContent;
